import React, { useState } from "react";

// =====================================================================
// TOTW — mobile filter bottom sheet (frame 1g). Country / Competition /
// Season rows expand into inline pickers; the round grid (5 columns)
// sits below, then the full-width "Show Team of the Week" button.
// Reset returns to the default (first country/competition, newest
// season, latest round) via onReset.
// =====================================================================

const SheetRow = ({ label, value, expanded, onToggle }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onToggle}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle();
      }
    }}
    className="flex items-center justify-between bg-[#F8FAFC] border border-[#EDF1F6] rounded-xl cursor-pointer"
    style={{ minHeight: 52, padding: "0 14px" }}
  >
    <span className="font-extrabold uppercase text-[#94A3B8]" style={{ fontSize: 11, letterSpacing: "0.07em" }}>
      {label}
    </span>
    <span className="flex items-center gap-[7px] font-bold text-[#0F172A]" style={{ fontSize: 13.5 }}>
      <span data-no-translate="true" className="whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: 170 }}>
        {value}
      </span>
      <span className="text-[#94A3B8] font-extrabold">{expanded ? "▾" : "›"}</span>
    </span>
  </div>
);

const SheetOptions = ({ options, selectedId, onPick }) => (
  <div className="bg-white border border-[#EDF1F6] rounded-xl overflow-auto" style={{ maxHeight: 210, padding: 6 }}>
    {options.map((option) => (
      <div
        key={option.id ?? "null"}
        role="button"
        tabIndex={0}
        onClick={() => onPick(option)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onPick(option);
          }
        }}
        className={`flex items-center justify-between rounded-lg cursor-pointer font-semibold ${
          option.id === selectedId ? "text-[#2283FF]" : "text-[#0F172A]"
        }`}
        style={{ padding: "10px 10px", fontSize: 13.5 }}
      >
        <span data-no-translate={option.noTranslate ? "true" : undefined}>{option.label}</span>
        {option.id === selectedId && <span className="font-extrabold">✓</span>}
      </div>
    ))}
  </div>
);

const TOTWBottomSheet = ({
  open,
  countries = [],
  selection,
  rounds = [],
  onPickCountry,
  onPickCompetition,
  onPickSeason,
  onPickRound,
  onReset,
  onClose,
}) => {
  const [expanded, setExpanded] = useState(null);
  if (!open) return null;

  const country = countries.find((c) => c.id === selection.countryId) || null;
  const competitions = country?.competitions || [];
  const competition = competitions.find((c) => c.id === selection.competitionId) || null;
  const seasons = competition?.seasons || [];
  const season = seasons.find((s) => s.id === selection.seasonId) || null;
  const latestUuid = rounds.length ? rounds[rounds.length - 1].uuid : null;
  const showingLatest = selection.roundUuid === latestUuid;

  const toggle = (key) => setExpanded((prev) => (prev === key ? null : key));

  return (
    <div className="fixed inset-0 z-[80]" style={{ background: "rgba(11,35,56,0.5)" }} onClick={onClose}>
      <div
        className="absolute left-0 right-0 bottom-0 bg-white overflow-auto"
        style={{ borderRadius: "20px 20px 0 0", padding: "10px 18px 22px", boxShadow: "0 -12px 40px rgba(4,18,33,0.28)", maxHeight: "88vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rounded-full bg-[#E2E8F0]" style={{ width: 38, height: 4, margin: "0 auto 14px" }} />
        <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
          <div className="font-extrabold text-[#0F172A]" style={{ fontSize: 16 }}>
            Filters
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={onReset}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onReset();
              }
            }}
            className="font-bold text-[#2283FF] cursor-pointer"
            style={{ fontSize: 12.5 }}
          >
            Reset
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <SheetRow label="Country" value={country?.name || "International"} expanded={expanded === "country"} onToggle={() => toggle("country")} />
          {expanded === "country" && (
            <SheetOptions
              options={countries.map((c) => ({ id: c.id, label: c.name || "International", noTranslate: Boolean(c.name), raw: c }))}
              selectedId={selection.countryId}
              onPick={(option) => {
                onPickCountry(option.raw);
                setExpanded(null);
              }}
            />
          )}
          <SheetRow label="Competition" value={competition?.name || ""} expanded={expanded === "competition"} onToggle={() => toggle("competition")} />
          {expanded === "competition" && (
            <SheetOptions
              options={competitions.map((c) => ({ id: c.id, label: c.name || "", noTranslate: true, raw: c }))}
              selectedId={selection.competitionId}
              onPick={(option) => {
                onPickCompetition(option.raw);
                setExpanded(null);
              }}
            />
          )}
          <SheetRow label="Season" value={season?.year || season?.id || ""} expanded={expanded === "season"} onToggle={() => toggle("season")} />
          {expanded === "season" && (
            <SheetOptions
              options={seasons.map((s) => ({ id: s.id, label: s.year || s.id, noTranslate: true, raw: s }))}
              selectedId={selection.seasonId}
              onPick={(option) => {
                onPickSeason(option.raw);
                setExpanded(null);
              }}
            />
          )}
        </div>
        <div className="font-extrabold uppercase text-[#94A3B8]" style={{ fontSize: 11, letterSpacing: "0.07em", margin: "16px 0 8px" }}>
          Round
        </div>
        <div className="grid gap-[7px]" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
          {rounds.map((round) => {
            const selected = round.uuid === selection.roundUuid;
            return (
              <div
                key={round.uuid}
                role="button"
                tabIndex={0}
                onClick={() => onPickRound(round)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPickRound(round);
                  }
                }}
                data-no-translate="true"
                className={`flex items-center justify-center rounded-[11px] font-bold cursor-pointer ${
                  selected ? "bg-[#2283FF] text-white border border-[#2283FF]" : "bg-white text-[#334155] border border-[#E2E8F0]"
                }`}
                style={{ height: 46, fontSize: 13.5 }}
              >
                {round.name || round.round || ""}
              </div>
            );
          })}
        </div>
        {showingLatest && (
          <div className="font-semibold text-[#94A3B8]" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.4 }}>
            Showing the latest available round.
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center w-full bg-[#2283FF] text-white font-extrabold cursor-pointer"
          style={{ height: 50, marginTop: 16, borderRadius: 12, fontSize: 14.5 }}
        >
          Show Team of the Week
        </button>
      </div>
    </div>
  );
};

export default TOTWBottomSheet;
